// /pages/HR/components/filters/LocationFilter.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './LocationFilter.css';

// Default location data with metadata - already outside component
const DEFAULT_LOCATIONS = [
  { code: 'FL', name: 'Florida', count: 0, timezone: 'EST' },
  { code: 'MA', name: 'Massachusetts', count: 0, timezone: 'EST' },
  { code: 'ME', name: 'Maine', count: 0, timezone: 'EST' },
  { code: 'NH', name: 'New Hampshire', count: 0, timezone: 'EST' },
  { code: 'NY', name: 'New York', count: 0, timezone: 'EST' },
  { code: 'RI', name: 'Rhode Island', count: 0, timezone: 'EST' },
];

/**
 * Location Filter Component
 * Multi-select dropdown for filtering by office locations
 */
const LocationFilter = React.memo(({
  locations = [],
  selectedLocations = [],
  onLocationChange = () => {},
  placeholder = 'Select locations',
  allLabel = 'All Locations',
  searchable = true,
  multiSelect = true,
  showCounts = true,
  className = '',
  disabled = false,
  clearable = true,
  maxHeight = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(selectedLocations);
  const [locationData, setLocationData] = useState([]);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Initialize location data
  useEffect(() => {
    if (locations.length > 0) {
      if (typeof locations[0] === 'string') {
        setLocationData(locations.map(loc => {
          const defaultLoc = DEFAULT_LOCATIONS.find(d => d.code === loc);
          return defaultLoc || { code: loc, name: loc, count: 0 };
        }));
      } else {
        setLocationData(locations);
      }
    } else {
      setLocationData(DEFAULT_LOCATIONS);
    }
  }, [locations]);

  // Filter locations based on search - memoized
  const filteredLocations = useMemo(() => {
    if (!searchTerm) return locationData;
    
    const searchLower = searchTerm.toLowerCase();
    return locationData.filter(location =>
      location.code.toLowerCase().includes(searchLower) ||
      location.name.toLowerCase().includes(searchLower)
    );
  }, [locationData, searchTerm]);

  // Handle location selection - memoized
  const handleLocationToggle = useCallback((locationCode) => {
    setSelected(prevSelected => {
      let newSelected;
      
      if (multiSelect) {
        if (prevSelected.includes(locationCode)) {
          newSelected = prevSelected.filter(code => code !== locationCode);
        } else {
          newSelected = [...prevSelected, locationCode];
        }
      } else {
        newSelected = prevSelected.includes(locationCode) ? [] : [locationCode];
        setIsOpen(false);
      }
      
      onLocationChange(newSelected);
      return newSelected;
    });
  }, [multiSelect, onLocationChange]);

  // Select/Deselect all - memoized
  const handleSelectAll = useCallback(() => {
    const allCodes = filteredLocations.map(loc => loc.code);
    const allSelected = allCodes.every(code => selected.includes(code));
    
    let newSelected;
    if (allSelected) {
      newSelected = selected.filter(code => !allCodes.includes(code));
    } else {
      newSelected = [...new Set([...selected, ...allCodes])];
    }
    
    setSelected(newSelected);
    onLocationChange(newSelected);
  }, [filteredLocations, selected, onLocationChange]);

  // Clear selection - memoized
  const handleClear = useCallback(() => {
    setSelected([]);
    setSearchTerm('');
    onLocationChange([]);
  }, [onLocationChange]);

  // Get display text - memoized
  const displayText = useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === locationData.length) {
      return allLabel;
    }
    if (selected.length === 1) {
      const location = locationData.find(loc => loc.code === selected[0]);
      return location ? location.name : selected[0];
    }
    return `${selected.length} locations selected`;
  }, [selected, locationData, placeholder, allLabel]);

  // Toggle dropdown - memoized
  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  // Handle search change - memoized
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Check if all filtered locations are selected - memoized
  const allFilteredSelected = useMemo(() => 
    filteredLocations.length > 0 && 
    filteredLocations.every(loc => selected.includes(loc.code)),
    [filteredLocations, selected]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Update local state when props change
  useEffect(() => {
    setSelected(selectedLocations);
  }, [selectedLocations]);

  return (
    <div 
      ref={dropdownRef}
      className={`location-filter ${className} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      {/* Filter Trigger */}
      <button
        className="location-trigger"
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span className="location-text">
          {displayText}
        </span>
        {selected.length > 0 && !disabled && (
          <span className="location-badge">{selected.length}</span>
        )}
        <span className="dropdown-arrow">▼</span>
      </button>

      {/* Clear Button */}
      {clearable && selected.length > 0 && !disabled && (
        <button
          className="location-clear"
          onClick={handleClear}
          title="Clear location filter"
        >
          ✕
        </button>
      )}

      {/* Dropdown Content */}
      {isOpen && (
        <div 
          className="location-dropdown"
          style={{ maxHeight }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="location-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          )}

          {/* Select All Option */}
          {multiSelect && filteredLocations.length > 0 && (
            <div className="location-actions">
              <button
                className="select-all-button"
                onClick={handleSelectAll}
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
                {searchTerm && ` (${filteredLocations.length})`}
              </button>
            </div>
          )}

          {/* Location List */}
          <div className="location-list">
            {filteredLocations.length === 0 ? (
              <div className="no-results">
                No locations found matching "{searchTerm}"
              </div>
            ) : (
              filteredLocations.map(location => (
                <label
                  key={location.code}
                  className={`location-item ${selected.includes(location.code) ? 'selected' : ''}`}
                >
                  <input
                    type={multiSelect ? "checkbox" : "radio"}
                    checked={selected.includes(location.code)}
                    onChange={() => handleLocationToggle(location.code)}
                    className="location-checkbox"
                  />
                  <span className="location-details">
                    <span className="location-name">{location.name}</span>
                    <span className="location-code">({location.code})</span>
                  </span>
                  {showCounts && location.count > 0 && (
                    <span className="location-count">{location.count}</span>
                  )}
                </label>
              ))
            )}
          </div>

          {/* Summary Footer */}
          {multiSelect && selected.length > 0 && (
            <div className="location-footer">
              <span className="selection-summary">
                {selected.length} of {locationData.length} selected
              </span>
              <button
                className="clear-selection"
                onClick={handleClear}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

LocationFilter.displayName = 'LocationFilter';

export default LocationFilter;